import { RelativityMetadata } from '../../../../backend/src/integrations/relativity/relativity-metadata.entity';
import formatDate from '@/helpers/formatDate';
import { Icon } from '@/new/components/icon';
import { Badge } from '@/new/components/shadcn/badge';
import { Label } from '@/new/components/shadcn/label';

interface RelativityMetadataDisplayProps {
  metadata: RelativityMetadata;
  compact?: boolean; // Use single column layout for popovers
}

// Helper function to convert camelCase to readable labels
const formatFieldLabel = (fieldName: string): string => {
  return fieldName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};

// Helper function to format file size
const formatFileSize = (bytes: number | null | undefined): string | null => {
  if (bytes === null || bytes === undefined) return null;
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

// Infer field type from value
const inferFieldType = (value: unknown): 'text' | 'date' | 'array' | 'number' => {
  if (Array.isArray(value)) return 'array';
  if (typeof value === 'number') return 'number';
  // Check if string looks like a date (ISO format)
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return 'date';
  return 'text';
};

export const RelativityMetadataDisplay = ({ metadata, compact = false }: RelativityMetadataDisplayProps) => {
  const fields = metadata.fields ?? {};

  // Build display fields from dedicated columns and JSONB fields
  const allFields: Array<{ label: string; value: unknown; type: 'text' | 'date' | 'array' | 'number' }> = [];

  // Add dedicated date columns first (if they have values)
  if (metadata.emailSentDate) {
    allFields.push({ label: 'Email Sent Date', value: metadata.emailSentDate, type: 'date' });
  }
  if (metadata.documentDate) {
    allFields.push({ label: 'Document Date', value: metadata.documentDate, type: 'date' });
  }

  // Add all JSONB fields dynamically
  Object.entries(fields).forEach(([key, value]) => {
    if (value === null || value === undefined) return;

    // Special handling for fileSize
    if (key === 'fileSize') {
      allFields.push({ label: formatFieldLabel(key), value: formatFileSize(value as number), type: 'text' });
      return;
    }

    allFields.push({ label: formatFieldLabel(key), value, type: inferFieldType(value) });
  });

  // If no fields to display, don't render anything
  if (allFields.length === 0) {
    return null;
  }

  const renderFieldValue = (field: { label: string; value: unknown; type: 'text' | 'date' | 'array' | 'number' }) => {
    switch (field.type) {
      case 'date':
        return <div className="text-sm text-gray-900">{formatDate(field.value as Date | string)}</div>;
      case 'array':
        if (Array.isArray(field.value) && field.value.length > 0) {
          return (
            <div className="flex flex-wrap gap-1">
              {field.value.map((item, index) => (
                <Badge key={index} className="bg-purple-100/60 text-xs text-purple-700">
                  {String(item)}
                </Badge>
              ))}
            </div>
          );
        }
        // Handle single value that should be displayed as array
        if (field.value !== null && field.value !== undefined && !Array.isArray(field.value)) {
          return (
            <div className="flex flex-wrap gap-1">
              <Badge className="bg-purple-100/60 text-xs text-purple-700">{String(field.value)}</Badge>
            </div>
          );
        }
        return <div className="text-sm text-gray-500">None</div>;
      case 'number':
        return (
          <div className="text-sm text-gray-900">
            {typeof field.value === 'number' ? field.value.toLocaleString() : String(field.value ?? '')}
          </div>
        );
      case 'text':
      default:
        // Handle long text values
        const textValue = String(field.value);
        if (textValue.length > 100) {
          return (
            <div className="text-sm break-words text-gray-900" title={textValue}>
              {textValue.substring(0, 100)}...
            </div>
          );
        }
        return <div className="text-sm break-words text-gray-900">{textValue}</div>;
    }
  };

  return (
    <div className={compact ? 'w-[400px] space-y-4' : 'space-y-4'}>
      <Label className="flex items-center gap-2 text-xs text-purple-600 uppercase">
        <Icon className="lucide--database h-4 w-4" />
        Relativity Metadata
      </Label>
      <div
        className={`rounded-md border border-purple-200 bg-purple-50/30 p-4 ${
          compact ? 'grid grid-cols-1 gap-y-3' : 'grid grid-cols-2 gap-x-6 gap-y-4'
        }`}
      >
        {allFields.map((field, index) => (
          <div key={index} className="min-w-0 space-y-1">
            <Label className="text-xs font-medium tracking-wide text-gray-500 uppercase">{field.label}</Label>
            {renderFieldValue(field)}
          </div>
        ))}
      </div>
    </div>
  );
};
